/**
 * Pure helpers for the DevOps Overview — grouping, attention vocabulary, the
 * last-build chip palette and relative time. Shared by the store (computeds)
 * and the body (rendering) so the two can never disagree about what a card
 * says. Everything here is a function of the `devops-overview` response;
 * nothing reaches the network or the stores.
 */
import type {
  DevOpsAttention,
  DevOpsOverviewClient,
  DevOpsOverviewProject,
} from "../devops.types";

/** The two activity windows the header toggle offers (the API defaults to 7). */
export type DevOpsOverviewWindow = 7 | 30;

export const DEVOPS_OVERVIEW_WINDOWS: readonly DevOpsOverviewWindow[] = [7, 30];

/** Group id for projects that no `devops_links` row attributes to a client. */
export const UNASSIGNED_GROUP_ID = "__unassigned__";
export const UNASSIGNED_GROUP_LABEL = "Unassigned to a client";

/** One card group: a client and the projects linked to it. */
export interface DevOpsOverviewGroup {
  /** The client id, or {@link UNASSIGNED_GROUP_ID} for the trailing group. */
  id: string;
  name: string;
  code: string | null;
  unassigned: boolean;
  /**
   * The CLIENT's own attention states, from the overview's `clients` row —
   * not its projects'. Empty for the unassigned group (no client) and
   * whenever the response lists no row for this client.
   */
  attention: DevOpsAttention[];
  projects: DevOpsOverviewProject[];
}

/**
 * The attention kinds a PROJECT can carry. `unattributed-activity` is
 * deliberately excluded: the API sets it on a CLIENT (its counts are a floor,
 * because a shared project's activity matched none of that client's links)
 * and never on a project row. Counting it over projects would print a
 * permanently-zero chip that reads as "no client is affected" — a confident
 * claim about something the strip cannot see. The client surface for it is
 * the group header (see {@link DevOpsOverviewGroup.attention}).
 */
export type DevOpsProjectAttention = Exclude<DevOpsAttention, "unattributed-activity">;

/** Display order of the attention strip — worst first, "no activity" last. */
export const ATTENTION_KINDS: readonly DevOpsProjectAttention[] = [
  "failed-build",
  "no-links",
  "module-mapping-mismatch",
  "no-subscriptions",
  "no-activity",
];

export type AttentionChipColor = "error" | "warning" | "info" | "default";

export interface AttentionMeta {
  /** Strip label ("Failed builds"). */
  label: string;
  /** Card badge — a function of the window because "no activity" names it. */
  badge: (windowDays: number) => string;
  color: AttentionChipColor;
}

/**
 * What each attention kind means to a person. `no-activity` is deliberately
 * NEUTRAL: a project with no events in the window is a state we cannot judge,
 * not a healthy one — the plan's acceptance line, and the reason it never gets
 * a green chip anywhere on this page.
 */
export const ATTENTION_META: Record<DevOpsAttention, AttentionMeta> = {
  "failed-build": {
    label: "Failed builds",
    badge: () => "last build failed",
    color: "error",
  },
  "no-links": {
    label: "Unlinked",
    badge: () => "not linked to a client",
    color: "warning",
  },
  "module-mapping-mismatch": {
    label: "Mapping mismatch",
    badge: () => "routing and links disagree",
    color: "warning",
  },
  "no-subscriptions": {
    label: "No webhooks",
    badge: () => "no webhooks",
    color: "info",
  },
  "no-activity": {
    label: "No activity",
    badge: (windowDays) => `no activity in ${windowDays} days`,
    color: "default",
  },
  // Not a fault: it says the numbers beside it are a floor, because a shared
  // project's events matched no link of this client's and belong to nobody
  // in particular. Only ever set on a CLIENT.
  "unattributed-activity": {
    label: "Some activity unattributed",
    badge: () => "counts are a floor",
    color: "info",
  },
};

/** The strip's counts — PROJECT states only (see {@link DevOpsProjectAttention}). */
/**
 * Why a CLIENT carries a state, for the group header's tooltip. Keyed by the
 * full attention vocabulary but populated only with the states the API sets
 * on a client — today just `unattributed-activity`. A client state with no
 * entry falls back to its {@link ATTENTION_META} label rather than going
 * unexplained.
 */
export const CLIENT_ATTENTION_WHY: Partial<Record<DevOpsAttention, string>> = {
  "unattributed-activity":
    "Some activity on this client's projects is not attributable to this client — a shared project's events matched none of its links. Its figures here are a floor, not a total.",
};

export type AttentionCounts = Record<DevOpsProjectAttention, number>;

export function emptyAttentionCounts(): AttentionCounts {
  return {
    "failed-build": 0,
    "no-links": 0,
    "module-mapping-mismatch": 0,
    "no-subscriptions": 0,
    "no-activity": 0,
  };
}

/** How many projects carry each attention kind (a project can carry several). */
export function countAttention(projects: readonly DevOpsOverviewProject[]): AttentionCounts {
  const counts = emptyAttentionCounts();
  for (const project of projects) {
    for (const kind of project.attention) {
      if (kind in counts) counts[kind] += 1;
    }
  }
  return counts;
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/**
 * What a card, a sort or a search calls the project. The overview can carry
 * a project by NAME only (webhook events that never named an id) or by ID
 * only (a subscription row scoped by GUID with no name recorded), so a
 * name-only or id-only project is displayed by what HQ knows of it — the
 * name when there is one, else the id, else a plain placeholder. Every place
 * that renders, sorts or searches an overview project's name goes through
 * here rather than `.name`, which is `string | null` on the wire.
 */
export function projectLabel(
  project: Pick<DevOpsOverviewProject, "name" | "projectId">
): string {
  return project.name ?? project.projectId ?? "Unnamed project";
}

/**
 * Projects grouped by client, in the order the page shows them: clients A→Z
 * (case-insensitive), projects A→Z within each, and the projects with no
 * client link together in a trailing "Unassigned to a client" group. A
 * project linked to several clients appears under EACH of them — the card is
 * about the project, the group is about who it serves, and hiding it from a
 * second client would make that client's view lie.
 */
export function groupProjectsByClient(
  projects: readonly DevOpsOverviewProject[],
  clients: readonly DevOpsOverviewClient[] = []
): DevOpsOverviewGroup[] {
  const byClient = new Map<string, DevOpsOverviewGroup>();
  const unassigned: DevOpsOverviewProject[] = [];
  // The CLIENT rows carry the client's own attention (`unattributed-activity`
  // among them); the per-project `clients` refs carry only id/name/code. The
  // group header is the one place a client state can be said, so it is looked
  // up here rather than inferred from the projects underneath it.
  const attentionOf = new Map(clients.map((c) => [c.id, c.attention ?? []]));

  for (const project of projects) {
    if (project.clients.length === 0) {
      unassigned.push(project);
      continue;
    }
    // A link table can carry the same client twice for one project (a repo
    // link and a project link); one group per client regardless.
    const seen = new Set<string>();
    for (const client of project.clients) {
      if (seen.has(client.id)) continue;
      seen.add(client.id);
      let group = byClient.get(client.id);
      if (!group) {
        group = {
          id: client.id,
          name: client.name,
          code: client.code ?? null,
          unassigned: false,
          attention: attentionOf.get(client.id) ?? [],
          projects: [],
        };
        byClient.set(client.id, group);
      }
      group.projects.push(project);
    }
  }

  const groups = Array.from(byClient.values()).sort((a, b) => byName(a.name, b.name));
  for (const group of groups) {
    group.projects.sort((a, b) => byName(projectLabel(a), projectLabel(b)));
  }
  if (unassigned.length > 0) {
    groups.push({
      id: UNASSIGNED_GROUP_ID,
      name: UNASSIGNED_GROUP_LABEL,
      code: null,
      unassigned: true,
      // No client, so no client state to report.
      attention: [],
      projects: unassigned.sort((a, b) => byName(projectLabel(a), projectLabel(b))),
    });
  }
  return groups;
}

/**
 * Case-insensitive search over what a person would type to find a project:
 * its name (or its id, for an id-only project — see {@link projectLabel}),
 * its clients (name or code), its modules (display name or slug) and the
 * names of its linked repos/pipelines.
 */
export function projectMatchesSearch(
  project: DevOpsOverviewProject,
  query: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack: string[] = [projectLabel(project)];
  for (const client of project.clients) {
    haystack.push(client.name);
    if (client.code) haystack.push(client.code);
  }
  for (const module of project.modules) {
    haystack.push(module.displayName, module.governedSlug);
  }
  for (const link of project.links) {
    if (link.entityName) haystack.push(link.entityName);
  }
  return haystack.some((s) => s.toLowerCase().includes(q));
}

/**
 * The API's report on the optional LIVE project read: `fetched` = every
 * connection asked answered, `partial` = what was read is known incomplete,
 * `note` = the server's own sentence about either. The two are independent —
 * one connection can refuse while another is truncated — so every renderer
 * below covers all four combinations rather than an if/else pair.
 */
export interface DevOpsLiveRead {
  fetched: boolean;
  partial: boolean;
  note: string | null;
}

/**
 * Whether the live read reported ANYTHING worth saying: the server's own
 * sentence, or `partial` (a definite fact even if the wording came back
 * empty). `fetched: false` ALONE is deliberately not enough — it is equally
 * "the live list was never asked for", which is the Overview's normal state
 * (its find sends no `includeLive`), and printing "could not be read" on
 * every page view would be a confident falsehood. Against the current API the
 * distinction costs nothing: a real failure always carries a note.
 */
function liveReadHasSomethingToSay(live: DevOpsLiveRead | null | undefined): live is DevOpsLiveRead {
  return !!live && (!!live.note || live.partial);
}

/** The server's wording, or an honest stand-in when a flag came back bare. */
const liveReadDetail = (live: DevOpsLiveRead): string =>
  live.note ?? "the server gave no detail.";

/**
 * The Overview caption's whole sentence, or null when the live read reported
 * nothing. The note is quoted verbatim; only the LABEL in front of it is
 * ours, and it is chosen from the FLAGS — never inferred from the note, since
 * a failed read carries one too and both flags can hold at once.
 *
 * Pure so the wording is testable: this repo has no component tests, and a
 * caption that silently dropped half of a mixed response is exactly the bug
 * that went unnoticed.
 */
export function liveReadCaption(live: DevOpsLiveRead | null | undefined): string | null {
  if (!liveReadHasSomethingToSay(live)) return null;
  const label =
    live.partial && !live.fetched
      ? "Live project list is incomplete and could not be read for every connection"
      : live.partial
        ? "Live project list is incomplete"
        : !live.fetched
          ? "Live project list could not be read"
          : "Live project list";
  return `${label}: ${liveReadDetail(live)}`;
}

/**
 * The lead sentence Project 360's not-found state puts in front of the same
 * note — the same four cases, said from the point of view of ONE project the
 * live list does not mention. (That page DOES ask `includeLive`, so a failed
 * read there is a fact about this read.)
 */
export function liveReadNotFoundLead(live: DevOpsLiveRead | null | undefined): string {
  if (live?.partial && !live.fetched) {
    return "The live project list could not be read for every connection, and what was read was truncated, so this project may simply not have been in it — ";
  }
  if (live?.partial) {
    return "The live project list was partial for this read, so this project may simply not have been in it — ";
  }
  if (live && !live.fetched) {
    return "The live project list could not be read, so this page is drawn from local records only — the server said: ";
  }
  return "The server's note on the live project list: ";
}

/**
 * The whole sentence Project 360's not-found state renders, or null when the
 * live read reported nothing (an older API with no `live` block included —
 * silence beats inventing a failure).
 */
export function liveReadNotFoundText(live: DevOpsLiveRead | null | undefined): string | null {
  if (!liveReadHasSomethingToSay(live)) return null;
  return `${liveReadNotFoundLead(live)}${liveReadDetail(live)}`;
}

export interface BuildResultMeta {
  label: string;
  color: "success" | "error" | "warning" | "default";
}

// Azure BuildResult enum codes, in case the persisted `result` arrived as the
// number rather than the name (the API accepts both; so does this chip).
const AZ_BUILD_RESULT_NAMES: Record<string, string> = {
  "2": "succeeded",
  "4": "partiallySucceeded",
  "8": "failed",
  "32": "canceled",
};

const BUILD_RESULT_META: Record<string, BuildResultMeta> = {
  succeeded: { label: "Succeeded", color: "success" },
  partiallySucceeded: { label: "Partial", color: "warning" },
  failed: { label: "Failed", color: "error" },
  canceled: { label: "Canceled", color: "default" },
};

/**
 * The last-build chip. `null` (no `build.complete` in the window) is a neutral
 * "no builds" — never green. An unknown result string is shown verbatim rather
 * than mapped to a colour it did not earn.
 */
export function buildResultMeta(result: string | null | undefined): BuildResultMeta {
  if (result == null || result === "") {
    return { label: "No builds", color: "default" };
  }
  const name = AZ_BUILD_RESULT_NAMES[String(result)] ?? String(result);
  return BUILD_RESULT_META[name] ?? { label: name, color: "default" };
}

/** Compact relative timestamp ("5m ago", "3h ago", "2d ago", then a date). */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const deltaSec = Math.round((now - then) / 1000);
  if (deltaSec < 60) return "just now";
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
